import type { SxProps } from "@mui/material";
import { invariant } from "~/lib/invariant";
import type { Topic } from "~/lqs";
import { usePlayerActions } from "../actions";
import { useLoadedPlayerTopics } from "../hooks";
import type { InitializedPanel, ThreeDPanel } from "../panels";
import { supportsVisualization, useAvailableTopics } from "../panels";
import { TopicSelect } from "./topic-select";

export function SourceTopicSelect({
  panel,
  topic,
  sx,
}: {
  panel: InitializedPanel;
  topic: Topic;
  sx?: SxProps;
}) {
  const playerTopics = useLoadedPlayerTopics();

  const playerActions = usePlayerActions();

  function handleTopicChange(newTopic: Topic): void {
    playerActions.changeTopic(panel, newTopic);
  }

  const availableTopics = playerTopics.filter((playerTopic) =>
    supportsVisualization(playerTopic.typeName, panel.visualization),
  );

  return (
    <TopicSelect
      inputLabel="Source topic"
      topics={availableTopics}
      disableClearing
      value={topic}
      onChange={handleTopicChange}
      sx={sx}
    />
  );
}

export function SourceTopicSelectV2({
  panel,
  topic,
  disableClearing,
  sx,
}: {
  panel: ThreeDPanel;
  topic?: Topic;
  disableClearing?: boolean;
  sx?: SxProps;
}) {
  const topics = useAvailableTopics(panel);

  const playerActions = usePlayerActions();

  if (topic == null) {
    const handleChange = (newTopic: Topic | null): void => {
      invariant(newTopic != null, "Expected new topic to never be null");

      playerActions.addTopic(panel, newTopic);
    };

    return (
      <TopicSelect
        inputLabel="Topic"
        topics={topics}
        value={null}
        onChange={handleChange}
        sx={sx}
      />
    );
  } else {
    const handleChange = (newTopic: Topic | null): void => {
      if (newTopic == null) {
        playerActions.removeTopic(panel, topic);
      } else {
        playerActions.addTopic(panel, newTopic);
      }
    };

    if (disableClearing) {
      return (
        <TopicSelect
          inputLabel="Topic"
          topics={topics}
          value={topic}
          disableClearing
          onChange={handleChange}
          sx={sx}
        />
      );
    } else {
      return (
        <TopicSelect
          inputLabel="Topic"
          topics={topics}
          value={topic}
          onChange={handleChange}
          sx={sx}
        />
      );
    }
  }
}
